import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  input,
  output,
  viewChild
} from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { ViewTab } from '../../../models/view.model';
import { GridSettings } from '../services/engine-view-settings.service';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NgTemplateOutlet } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { ViewSelectorComponent } from './view-selector/view-selector.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-engine-view-toolbar',
    templateUrl: './engine-view-toolbar.component.html',
    styleUrls: ['./engine-view-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        NgTemplateOutlet,
        MatButton,
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatTooltip,
        ViewSelectorComponent,
        TranslatePipe,
    ]
})
export class EngineViewToolbarComponent implements OnInit, OnDestroy {
  private _destroy$: Subject<boolean> = new Subject<boolean>();
  globalSearchControl = new UntypedFormControl('');

  readonly disabled = input(false);
  readonly isCollapsed = input(false);
  readonly settings = input<GridSettings>(undefined);
  @Input() set globalSearch(value: string) {
    if ((value || '') != (this.globalSearchControl.value || '')) {
      this.globalSearchControl.setValue(value);
      if (value) {
        this.changeGlobalSearchVisibility(true);
      }
    }
  }
  readonly viewTabs = input<ViewTab[]>(undefined);
  readonly viewTabsSelectionMode = input<'multi' | 'single'>(undefined);
  readonly canBulkUpdate = input(false);

  readonly globalSearchChange = output<string>();
  readonly bulkUpdate = output<void>();
  readonly globalSearchVisibilityChange = output<boolean>();
  readonly filtersVisibilityChange = output<boolean>();
  readonly viewTabsVisibilityChange = output<ViewTab[]>();
  readonly refreshData = output<any>();
  readonly restoreSettings = output<any>();
  readonly navigateToSettings = output<any>();

  readonly globalSearchInput = viewChild<ElementRef>('globalSearch');
  ngOnInit() {
    this.globalSearchControl.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        debounceTime(300),
        tap((value) => {
          this.globalSearchChange.emit(value);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  changeGlobalSearchVisibility(value: boolean) {
    const globalSearchValue = this.globalSearchControl.value || '';
    if (!value && globalSearchValue != '') {
      this.globalSearchControl.setValue('');
    }
    this.globalSearchVisibilityChange.emit(value);
    if (!this.settings().isGlobalSearchEnabled) {
      setTimeout(() => {
        const globalSearchInput = this.globalSearchInput();
        if (globalSearchInput?.nativeElement) {
          globalSearchInput.nativeElement.focus();
        }
      });
    }
  }

  changeFiltersVisibility(value: boolean) {
    const globalSearchValue = this.globalSearchControl.value || '';
    if (value && globalSearchValue != '') {
      this.globalSearchControl.setValue('');
    }

    this.filtersVisibilityChange.emit(value);
  }

  onRestoreSettingsClick() {
    this.globalSearchChange.emit(undefined);
    this.restoreSettings.emit(undefined);
  }

  // to prevent mat-tab focus
  stopInputPropagation(event: UIEvent): void {
    event.stopPropagation();
  }
}
